import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from '@angular/core';
import { isEmptyArray, isStringEmpty, parseEmptyArray } from '@common/helpers';
import { PAYMENT_DETAILS, POS_COUNTRY_CODE } from '@common/helpers/constants/my-booking/my_booking_itinerary.contant';
import { MasterDataS01P031 } from '@common/interfaces/mybooking-itinerary/mybooking-itinerary.interface';
import {
  Base,
  BidMyPrice,
  ChargeableLounge,
  ChargeableSeatRequest,
  ChargeableSeatRequestTaxes,
  Couch,
  CouchTaxes,
  FirstBaggage,
  FirstBaggageTaxes,
  FlightSurcharge,
  FuelSurcharge,
  InsuranceSurcharge,
  InsuranceSurchargeAndFuelSurcharge,
  KeepMyFare,
  KeepMyFareTaxes,
  LoungeTaxes,
  Meal,
  MealTaxes,
  Taxes,
  TicketingFee,
  UnitPriceItem,
} from '@common/interfaces/mybooking/order-prices.interface';
import { MyBookingItineraryService } from '@common/services/my-booking/my-booking-itinerary.service';
import { SupportComponent } from '@lib/components/support-class';
import { MOffice } from '@lib/interfaces';
import { AmountFormatPipe, StaticMsgPipe } from '@lib/pipes';
import { CommonLibService } from '@lib/services';
import { throws } from 'assert';
import { Subscription } from 'rxjs';
import {
  GetOrderResponseData,
  GetOrderResponseDataOrderType,
  GetOrderResponseDataPricesTotalPricesTicketPricesTotalInner,
  Traveler,
  TravelerNamesInner,
} from 'src/sdk-servicing';

@Component({
  selector: 'asw-payment-item',
  templateUrl: './payment-item.component.html',
  styleUrls: ['./payment-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaymentItemComponent extends SupportComponent {
  @Input() travelerInput!: Traveler;
  @Input() orderDataInputInput!: GetOrderResponseData;
  @Input() itemNumber!: number;
  @Input() lengthTraveler?: number;

  paymentInformations!: UnitPriceItem;
  mOffice!: MOffice;
  private _subscription = new Subscription();

  constructor(
    public change: ChangeDetectorRef,
    public _staticMsg: StaticMsgPipe,
    public _amountFormatPipe: AmountFormatPipe,
    private _common: CommonLibService,
    private _myBookingItineraryService: MyBookingItineraryService
  ) {
    super(_common);
  }

  reload(): void {}

  init(): void {
    this.paymentInformations = this._myBookingItineraryService.getpaymentInformationsByTravelerId(
      this.travelerInput?.id,
      this.orderDataInputInput
    );

    this._subscription.add(
      this._myBookingItineraryService.masterData$.subscribe((result: MasterDataS01P031) => {
        this.mOffice = result?.mOffice![0] ?? ({} as MOffice);
        this.change.markForCheck();
      })
    );
  }

  /** [1.9.22] 搭乗者姓名 */
  get passengerName(): TravelerNamesInner | undefined {
    return this.travelerInput.names?.[0] ?? undefined;
  }

  /** [1.9.23] 搭乗者種別 */
  get passengerType(): string {
    /**
     * 当該搭乗者種別="ADT"(大人)の場合、大人を示す文言を表示する。
     * 当該搭乗者種別="B15"(ヤングアダルト)の場合、ヤングアダルトを示す文言を表示する。
     * 当該搭乗者種別="CHD"(小児)の場合、小児を示す文言を表示する。
     * 当該搭乗者種別="INS"(席あり幼児)の場合、席あり幼児を示す文言を表示する。
     * 当該搭乗者種別="INF"(席なし幼児)の場合、席なし幼児を示す文言を表示する。
     */
    return this._myBookingItineraryService.getPassengerTypeLabel(this.travelerInput?.passengerTypeCode);
  }

  /** [1.9.24] 必要マイル数 */
  get requiredNumberMiles(): number {
    return this.paymentInformations?.ticketPrices?.requiredMileage ?? 0;
  }

  /** [1.9.24] 必要マイル数 */
  get isRequiredNumberMiles(): boolean {
    /** PNR情報取得API応答.data.orderType.isAwardBooking=true(特典PNR)の場合、当該搭乗者の必要マイル数を表示する。*/
    return Boolean(this.orderDataInputInput?.orderType?.isAwardBooking);
  }

  /** [1.9.25] 運賃・フライトサーチャージ */
  get isFaresOrFlightSurcharges(): boolean {
    return this._myBookingItineraryService.isChannelTypeNotEqualGdsOrNh3(this.orderDataInputInput);
  }

  /** [1.9.26] 運賃額 (運賃・フライトサーチャージ) */
  get faresAmount(): Array<GetOrderResponseDataPricesTotalPricesTicketPricesTotalInner> {
    /**
     * 1)currencyCodeに基づいて、運賃総額[] = 当該支払情報.ticketPrices.base
     * 2)<以下、運賃総額[]の要素数繰り返し>(それぞれの要素が当該運賃総額)
     * 当該運賃総額.currencyCodeを通貨コード、当該運賃総額.valueを金額として、表示する。
     * フォーマット:共通部品(G03-016)の金額フォーマット処理を参照
     * 通貨コードは改行で区切る。
     * <ここまで、運賃総額[]の要素数繰り返し>
     * ※運賃額が0の場合は金額0の表示を行う。
     */
    return (
      this.paymentInformations?.ticketPrices?.base ??
      ([] as Array<GetOrderResponseDataPricesTotalPricesTicketPricesTotalInner>)
    );
  }

  /** [1.9.26] 運賃額 (運賃・フライトサーチャージ) */
  get showFaresAmount(): boolean {
    /** PNR情報取得API応答.data.orderType.isAwardBooking=false(有償PNR)の場合 */
    return Boolean(this.orderDataInputInput?.orderType?.isAwardBooking) === false;
  }

  /** [1.9.29] 航空保険料 */
  get insuranceSurchargeTitle(): string {
    /** ※項目名について、""IS""を基に「イベントID(1)」で取得した税金リストをフィルタして、​項目名に税金名称を設定する。 */
    return this._myBookingItineraryService.getTaxName('IS');
  }

  /** [1.9.32] 税金・料金等詳細 */
  get isTaxFeeDetails(): boolean {
    return this._myBookingItineraryService.isChannelTypeNotEqualGdsOrNh3(this.orderDataInputInput);
  }

  /** [1.9.33] 各国税額 (税金・料金等詳細) */
  get isTaxAmountForEachCountry(): boolean {
    /** 当該支払情報.ticketPrices.taxesが存在する場合、表示する */
    return !isEmptyArray(this.paymentInformations?.ticketPrices?.taxes);
  }

  /** [1.9.34] 税金名 (各国税額) (税金・料金等詳細) */
  taxNameForEachCountry(tax?: Taxes): string {
    let taxName = this._staticMsg.transform(PAYMENT_DETAILS.OTHERS_TAX);
    /**
     * 1)当該支払情報.codeを基に「イベントID(1)」で取得した税金リストをフィルタして、税金名称を取得す
     * 2)当該支払情報.codeを基に「イベントID(1)」で取得した国コードリストをフィルタして、国コード(3レター)を取得す
     * 3)税金名称が存在する場合、税金名は国コード(3レター)+ 税金名称を表示する
     */
    const taxObj = this._myBookingItineraryService.getTaxByCode(tax?.code);
    if (!isStringEmpty(taxObj?.tax_name)) {
      taxName = `${String(taxObj?.country_3letter_code)} ${taxObj?.tax_name}`;
    }
    /** 4)税金名称が存在しない場合、その他税額を表示する */
    return taxName;
  }

  /** [1.9.35] 税額 (各国税額) (税金・料金等詳細) */
  taxAmountForEachCountry(tax?: Taxes): string {
    if (tax?.isExempted) {
      /** TODO: will change the mongoid 金額の代わりに免除されている旨を表示する */
      return this._staticMsg.transform(PAYMENT_DETAILS.NONE);
    }
    return this._amountFormatPipe.transform(tax?.value ?? 0, undefined, tax?.currencyCode);
  }

  /** [1.9.37] 税金名 (燃油特別付加運賃) (税金・料金等詳細) */
  get taxNameSpecialFuelSurcharge(): string {
    /** "F"を基に「イベントID(1)」で取得した税金リストをフィルタして、税金名称を取得する。*/
    return this._myBookingItineraryService.getTaxName('F');
  }

  /** [1.9.76] 税額 (航空保険料及び燃油特別付加運賃) (税金・料金等詳細) */
  get insuranceAndFuelSurcharge(): GetOrderResponseDataPricesTotalPricesTicketPricesTotalInner[] {
    let insuranceAndFuelSurcharge: GetOrderResponseDataPricesTotalPricesTicketPricesTotalInner[] = [];

    /**
     * ※POS国コード="MY"(マレーシア)、かつ税金コードが4桁(税金コードYQAC/YRVAが燃油特別付加運賃または税金コードYQAD/YRVBが航空保険料)が存在する場合、
     * currencyCodeに基づいて、航空保険料及び燃油特別付加運賃[]は当該支払情報.ticketPrices.fuelSurcharge[]+当該支払情報.ticketPrices.insuranceSurcharge[]とする。
     */
    if (
      this.mOffice?.pos_country_code === POS_COUNTRY_CODE.MY &&
      (!isEmptyArray(this.paymentInformations?.ticketPrices?.insuranceSurcharge) ||
        !isEmptyArray(this.paymentInformations?.ticketPrices?.fuelSurcharge))
    ) {
      insuranceAndFuelSurcharge = this._myBookingItineraryService.addValues(
        (this.paymentInformations?.ticketPrices?.fuelSurcharge as Array<FuelSurcharge>) ?? [],
        (this.paymentInformations?.ticketPrices?.insuranceSurcharge as Array<InsuranceSurcharge>) ?? []
      );

      /**
       * POS国コード≠("CA"(カナダ)、かつ"MY"(マレーシア))、かつ税金コードが2桁(税金コードYQが航空保険料及び燃油特別付加運賃)が存在する、
       * 航空保険料及び燃油特別付加運賃[]は当該支払情報.ticketPrices.insuranceSurchargeAndFuelSurcharge (※currencyCodeが同じ場合、ひとつのオブジェクトとし、valueを合算する。)
       */
    } else if (
      this.mOffice?.pos_country_code !== POS_COUNTRY_CODE.CA &&
      this.mOffice?.pos_country_code !== POS_COUNTRY_CODE.MY &&
      !isEmptyArray(this.paymentInformations?.ticketPrices?.insuranceSurchargeAndFuelSurcharge)
    ) {
      insuranceAndFuelSurcharge = this.sumByCurrencyCode(
        (this.paymentInformations?.ticketPrices
          ?.insuranceSurchargeAndFuelSurcharge as Array<InsuranceSurchargeAndFuelSurcharge>) ?? []
      );
    }

    return insuranceAndFuelSurcharge;
  }

  sumByCurrencyCode(data: any[]) {
    const result = data.reduce((acc, item) => {
      const existing = acc.find((i: { currencyCode: string }) => i.currencyCode === item.currencyCode);
      if (existing) {
        existing.value += item.value;
      } else {
        acc.push({ ...item });
      }
      return acc;
    }, [] as []);
    return result;
  }

  /** [1.9.44] 税金名 (フライトサーチャージ) (税金・料金等詳細) */
  get taxAmountflightSurcharge(): Array<FlightSurcharge> {
    return this.paymentInformations?.ticketPrices?.flightSurcharge ?? ([] as Array<FlightSurcharge>);
  }

  /**
   * ※POS国コード="CA"(がカナダ)の場合、航空会社の金額であるため、運賃・フライトサーチャージグループ中に表示する
   * Show item 45 in group 運賃・フライトサーチャージ (Fare and flight surcharge)
   */
  get showTicketingFeeDiffCanada(): boolean {
    /**
     * POS国コード≠"CA"(がカナダ)の場合、航空会社の金額であるため、税金・料金等詳細グループ中に表示する
     * Show item 45 in group 税金・料金等詳細 (Tax, fee, etc. details)
     */
    return this.mOffice?.pos_country_code !== POS_COUNTRY_CODE.CA;
  }

  /** [1.9.45] 発券手数料、交換発行手数料 (税金・料金等詳細) */
  get isTicketingExchangeFee(): boolean {
    /** PNR情報取得API応答.data.orderType.channelType≠""gds"" (GDS PNR)、かつ当該支払情報.ticketPrices.ticketingFee[]が存在する場合、表示する */
    return (
      this.orderDataInputInput?.orderType?.channelType !== GetOrderResponseDataOrderType.ChannelTypeEnum.Gds &&
      !isEmptyArray(this.paymentInformations?.ticketingFee)
    );
  }

  /** [1.9.46] 税金名 (発券手数料、交換発行手数料) (税金・料金等詳細) */
  get taxNameTicketingExchangeFee(): string {
    /** "OB"を基に「イベントID(1)」で取得した税金リストをフィルタして、税金名称を取得する */
    return this._myBookingItineraryService.getTaxName('OB');
  }

  /** [1.9.47] 税額 (発券手数料、交換発行手数料) (税金・料金等詳細) */
  get taxAmountTicketingExchangeFee(): Array<TicketingFee> {
    return this.paymentInformations?.ticketingFee ?? ([] as Array<TicketingFee>);
  }

  destroy() {
    if (this._subscription) {
      this._subscription.unsubscribe();
    }
  }
}
